// ── Candidate match readiness — pure state derivation ────────────────────────
//
// Distinguishes WHY a candidate sees no matches instead of collapsing every
// cause into one generic "No matches" message. Mirrors the split used by
// resume-clarity.ts: this file is pure + framework-free (no DB, no classifier,
// no clock) so the API (which gathers the signals) and the web UI (which
// renders the message) share one source of truth.
//
// Two states this candidate-facing model deliberately does NOT cover, because
// they are not observable as a persisted server state given the current
// architecture (see docs/pilot/TARGET_ROLE_ONBOARDING.md):
//   - NO_ACTIVE_RESUME    — handled entirely by the route-level ResumeGate,
//                           which redirects away before any of this renders.
//   - RESUME_PROCESSING / PROCESSING_FAILED — POST /resumes is a single
//     synchronous request (no background job, no status column on `resumes`);
//     these are transient CLIENT states during that one request only
//     (see ResumeOnboarding.tsx's Phase union), never a state to poll for.
// A sixth state, NO_SAFE_MATCHES, is also client-local: there is no persisted
// "we already tried and found nothing" flag (adding one would need a schema
// migration this task does not justify), so the frontend derives it itself
// from the direct response of the (idempotent, $0) generate-matches action
// rather than from a GET.
//
// ready_to_generate no longer offers a manual button: the candidate's FIRST
// match set is generated automatically (no click) the moment this state is
// reached — see ensureFirstMatchGeneration (API) and MatchReadinessPanel.tsx
// (web), which fires it once via an effect and shows this state's copy as a
// loading message. A manual "Refresh Matches" action still exists — it's the
// pre-existing generateMatchesExplicit/POST /matches/generate path, now
// surfaced only (a) once matches already exist, and (b) as the retry
// affordance on the NO_SAFE_MATCHES state below.
/**
 * Priority order (most-actionable-first, mirrors resume-clarity's precedence):
 *   1. ambiguous role      — nothing downstream can be trusted until this resolves
 *   2. matching disabled   — an ops posture, not something the candidate can fix
 *   3. insufficient inventory — role is clear, but there's nothing to match against
 *   4. matchCount > 0      — matches already exist
 *   5. else                — role clear, inventory fine, nothing generated yet
 */
export function deriveCandidateMatchState(s) {
    if (s.clarityAmbiguous)
        return "target_role_clarification_required";
    if (!s.matchingEnabled)
        return "matching_disabled";
    if (s.familyCoverageGap !== "none")
        return "insufficient_inventory";
    if (s.matchCount > 0)
        return "matches_available";
    return "ready_to_generate";
}
/** Exact copy per Phase 8 of the task spec — one distinct message per state,
 *  never a single generic "No matches" line for every condition. */
export function candidateMatchStateMessage(state) {
    switch (state) {
        case "target_role_clarification_required":
            return {
                headline: "Choose your target role",
                body: "Your experience fits several directions. Choose the role you want Calibre to prioritise.",
                showGenerateAction: false,
            };
        case "matching_disabled":
            return {
                headline: "Matching temporarily unavailable",
                body: "Match generation is temporarily unavailable. Please try again later.",
                showGenerateAction: false,
            };
        case "insufficient_inventory":
            return {
                headline: "Limited inventory for your role",
                body: "We understand your target role, but there are not enough suitable active jobs yet.",
                showGenerateAction: false,
            };
        case "ready_to_generate":
            return {
                headline: "Finding your best matches…",
                body: "We'll compare your profile with active jobs using your skills, experience and preferences. Detailed AI scoring is not run automatically.",
                showGenerateAction: false,
            };
        case "matches_available":
            return {
                headline: "Matches available",
                body: "Your matches are ready below.",
                showGenerateAction: false,
            };
    }
}
/** The client-local state reached after an explicit generate action ran and
 *  genuinely found nothing — distinct from "ready_to_generate" (never tried)
 *  because it reflects the actual outcome of a real attempt, not a guess. */
export const NO_SAFE_MATCHES_MESSAGE = {
    headline: "No strong matches yet",
    body: "We did not find strong enough matches. We will not fill the list with unrelated jobs.",
    showGenerateAction: true,
};
//# sourceMappingURL=candidate-match-state.js.map